// src/components/RequireSubscription.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useAuth } from "../../AuthContext";
import {
  checkPaidJourneyAccessForCurrentUser,
  getCachedPaidJourneyAccessForUser,
  writePaidJourneyAccessSessionCache,
} from "../../lib/access/paidJourneyAccess";
import OnceOffPaymentPopup from "../payfast/OnceOffPaymentPopup";

const FREE_LEVEL_LIMIT = 9;

function getNumberFromKey(value) {
  if (!value) return null;

  const raw = String(value).trim();

  const match = raw.match(/(\d+)/);

  if (!match?.[1]) {
    return null;
  }

  const number = Number(match[1]);

  return Number.isFinite(number)
    ? number
    : null;
}

/*
 * Reads the level directly from URLs such as:
 *
 * /play/1/1-1/L1/welcome
 * /play/1/1-1/L9/result
 *
 * This means the free-level bypass does not depend
 * only on useParams().
 */
function getLevelNumberFromPath(pathname) {
  if (!pathname) return null;

  const parts = String(pathname)
    .split("/")
    .filter(Boolean);

  /*
   * Expected:
   *
   * play
   * questId
   * subquestId
   * levelKey
   * page
   */
  const playIndex = parts.findIndex(
    (part) =>
      part.toLowerCase() === "play"
  );

  if (playIndex < 0) {
    return null;
  }

  const possibleLevelKey =
    parts[playIndex + 3];

  return getNumberFromKey(
    possibleLevelKey
  );
}

function isFreeLevel(levelNumber) {
  return (
    Number.isFinite(levelNumber) &&
    levelNumber >= 1 &&
    levelNumber <= FREE_LEVEL_LIMIT
  );
}

function makeFreeLevelAccess(
  questNumber,
  levelNumber
) {
  return {
    allowed: true,
    reason: "free_level",
    showPayment: false,
    questNumber,
    levelNumber,
  };
}

function AccessCheckingScreen() {
  return (
    <div className="min-h-screen bg-[#FFF7ED] flex items-center justify-center px-4 font-sans">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 text-center shadow-xl border border-orange-100">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 text-3xl">
          ⏳
        </div>

        <h1 className="text-xl font-extrabold text-gray-900 mb-2">
          Checking access
        </h1>

        <p className="text-gray-600 leading-relaxed">
          Please wait while we check your journey access.
        </p>
      </div>
    </div>
  );
}

function QuestLockedScreen({
  access,
  onBuy,
}) {
  const navigate = useNavigate();

  const canShowPayment =
    access?.showPayment === true;

  return (
    <div className="min-h-screen bg-[#FFF7ED] flex items-center justify-center px-4 font-sans">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 text-center shadow-xl border border-orange-100">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 text-3xl">
          🔒
        </div>

        <h1 className="text-2xl font-extrabold text-gray-900 mb-2">
          Quest Locked
        </h1>

        <p className="text-gray-600 leading-relaxed mb-4">
          {access?.message ||
            "You’ve completed the free levels. Unlock the paid journey to continue learning."}
        </p>

        <div className="mb-6 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-left">
          <p className="text-sm font-bold text-gray-900">
            Want free access?
          </p>

          <p className="mt-1 text-xs leading-relaxed text-gray-600">
            Invite 5 SMMEs or entrepreneurs. Once they register through your
            invite link and use the app, you can unlock the once-off paid levels
            for free.
          </p>
        </div>

        {canShowPayment ? (
          <button
            type="button"
            onClick={onBuy}
            className="mb-3 w-full rounded-2xl bg-[#FF914D] px-5 py-3 font-bold text-white shadow-md hover:bg-[#ff7a1f] transition"
          >
            Unlock the 60-Day Journey
          </button>
        ) : null}

        <button
          type="button"
          onClick={() =>
            navigate("/profile", {
              replace: true,
            })
          }
          className="mb-3 w-full rounded-2xl border border-orange-200 bg-orange-50 px-5 py-3 font-bold text-[#FF914D] shadow-sm hover:bg-orange-100 transition"
        >
          View Invite Progress
        </button>

        <button
          type="button"
          onClick={() =>
            navigate("/dashboard", {
              replace: true,
            })
          }
          className="w-full rounded-2xl border border-orange-200 bg-white px-5 py-3 font-bold text-[#FF914D] shadow-sm hover:bg-orange-50 transition"
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}

export default function RequireSubscription({
  children,
}) {
  const {
    questId,
    levelKey,
  } = useParams();

  const location =
    useLocation();

  const {
    user,
  } = useAuth();

  const questNumber =
    useMemo(
      () =>
        getNumberFromKey(
          questId
        ),
      [questId]
    );

  /*
   * First try the React Router param.
   *
   * If that is unavailable, read L1/L2/etc.
   * directly from the actual URL.
   */
  const levelNumber =
    useMemo(() => {
      const fromParam =
        getNumberFromKey(
          levelKey
        );

      if (
        Number.isFinite(
          fromParam
        )
      ) {
        return fromParam;
      }

      return getLevelNumberFromPath(
        location.pathname
      );
    }, [
      levelKey,
      location.pathname,
    ]);

  /*
   * L1–L9 are outside the paid-access system.
   */
  const freeLevel =
    isFreeLevel(
      levelNumber
    );

  /*
 * If UserDashboard has already checked L10+,
 * read that result synchronously.
 *
 * No network request and no loading screen.
 */
const cachedPaidAccess =
  useMemo(() => {
    if (
      freeLevel ||
      !user?.id
    ) {
      return null;
    }

    return getCachedPaidJourneyAccessForUser(
      user.id
    );
  }, [
    freeLevel,
    user?.id,
    location.pathname,
  ]);

const [
  checking,
  setChecking,
] = useState(
  !freeLevel &&
    !cachedPaidAccess
);

const [
  access,
  setAccess,
] = useState(
  cachedPaidAccess
);

const [
  showPaymentPopup,
  setShowPaymentPopup,
] = useState(false);

useEffect(() => {
  let cancelled =
    false;

  async function checkAccess() {
    /*
     * ================================================
     * L1-L9
     * ================================================
     *
     * The paid-access system does not exist for them.
     */
    if (freeLevel) {
      setChecking(
        false
      );

      setAccess(
        null
      );

      setShowPaymentPopup(
        false
      );

      return;
    }

    /*
     * ================================================
     * CACHED PAID RESULT
     * ================================================
     *
     * The dashboard already checked access.
     *
     * Reuse it.
     * Do not check again.
     */
    if (cachedPaidAccess) {
      setAccess(
        cachedPaidAccess
      );

      setChecking(
        false
      );

      setShowPaymentPopup(
        false
      );

      return;
    }

    if (!user?.id) {
      setChecking(
        false
      );

      setAccess({
        allowed:
          false,

        reason:
          "not_authenticated",

        showPayment:
          false,

        message:
          "Please sign in again to continue.",
      });

      return;
    }

    /*
     * ================================================
     * DIRECT PAID-LEVEL URL
     * ================================================
     *
     * Only reaches here when:
     *
     * - level >= 10
     * - no session cache exists
     *
     * Example:
     * user manually opens /play/.../L10/welcome
     */
    setChecking(
      true
    );

    setShowPaymentPopup(
      false
    );

    try {
      const result =
        await checkPaidJourneyAccessForCurrentUser({
          questId:
            questNumber,

          levelNumber,

          userId:
            user.id,
        });

      if (cancelled) {
        return;
      }

      setAccess(
        result
      );
    } catch (error) {
      console.error(
        "Failed to check paid journey access:",
        error
      );

      if (cancelled) {
        return;
      }

      setAccess({
        allowed:
          false,

        reason:
          "access_check_failed",

        showPayment:
          false,

        message:
          "We could not check your access right now. Please try again in a moment.",
      });
    } finally {
      if (
        !cancelled
      ) {
        setChecking(
          false
        );
      }
    }
  }

  checkAccess();

  return () => {
    cancelled =
      true;
  };
}, [
  user?.id,
  freeLevel,
  cachedPaidAccess,
  questNumber,
  levelNumber,
]);
  /*
   * ----------------------------------------------------
   * HARD BYPASS
   * ----------------------------------------------------
   *
  *
 * L1-L9 can NEVER enter the paid-access UI.
 */
if (freeLevel) {
  return children;
}

/*
 * Use the dashboard's cached result when one exists.
 */
const effectiveAccess =
  cachedPaidAccess ||
  access;

/*
 * An already-authorised paid user enters
 * immediately. No checking screen.
 */
if (
  effectiveAccess?.allowed
) {
  return children;
}

/*
 * This should now only appear for a direct
 * uncached L10+ URL.
 */
if (checking) {
  return (
    <AccessCheckingScreen />
  );
}

  if (
    access?.allowed
  ) {
    return children;
  }

  return (
    <>
      <QuestLockedScreen
        access={
          effectiveAccess
        }
        onBuy={() =>
          setShowPaymentPopup(
            true
          )
        }
      />

      <OnceOffPaymentPopup
        open={
          showPaymentPopup
        }
        onClose={() =>
          setShowPaymentPopup(
            false
          )
        }
        onAccessAlreadyActive={() => {
          const activeAccess = {
            allowed:
              true,

            reason:
              "onceoff_paid_access",

            showPayment:
              false,
          };

          writePaidJourneyAccessSessionCache(
            user?.id,
            activeAccess
          );

          setAccess(
            activeAccess
          );

          setShowPaymentPopup(
            false
          );
        }}
      />
    </>
  );
}